import React, { useState, useRef, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ActivityForm from './components/ActivityForm';
import ActivityList from './components/ActivityList';
import ActivityStats from './components/ActivityStats';
import RekapAbsensi from './components/RekapAbsensi';
import DashboardPage from './components/DashboardPage';
import TicketReport from './components/TicketReport';
import UserManagement from './components/UserManagement';
import ErrorBoundary from './components/ErrorBoundary';
import ShortcutsHelpModal from './components/ShortcutsHelpModal';
import ThemeSwitcher from './components/ThemeSwitcher';
import TicketForm from './components/TicketForm';
import TicketList from './components/TicketList';
import TicketDetail from './components/TicketDetail';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import MasterBagian from './components/MasterBagian';
import MasterCabang from './components/MasterCabang';
import LogHistories from './components/LogHistories';
import HakAkses from './components/HakAkses';
import MasterAkun from './components/MasterAkun';
import TransaksiFinance from './components/TransaksiFinance';
import TransaksiForm from './components/TransaksiForm';
import JurnalUmum from './components/JurnalUmum';
import LaporanAkhir from './components/LaporanAkhir';
import CafeDashboard from './components/CafeDashboard';
import CafePOS from './components/CafePOS';
import CafeSales from './components/CafeSales';
import CafeReport from './components/CafeReport';
import PrintSettings from './components/PrintSettings';
import { AlertCircle, Loader2, Activity, LogOut, Users, Shield, User as UserIcon, Ticket, LayoutDashboard, ChevronDown, PlusSquare, FileBarChart, List, PlusCircle, Keyboard, Building2, History, Banknote, DollarSign, BookOpen, TrendingUp, Settings, MapPin, Coffee, ShoppingCart, Printer, HeartPulse, Monitor, UserCheck } from 'lucide-react';
import financeService from './services/finance';
import NetworkStatus from './components/NetworkStatus';
import UpdateNotification from './components/UpdateNotification';
import MobileLayout from './components/MobileLayout';
import MobileDashboard from './components/MobileDashboard';
import useIsMobile from './hooks/useIsMobile';
import { useActivities } from './hooks/useActivities';
import { useTickets } from './hooks/useTickets';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import { useTheme } from './context/ThemeContext';

function AppContent() {
  const [showRegister, setShowRegister] = useState(false);
  const isMobile = useIsMobile();
  const headerStyle = {
    background: `linear-gradient(to right, var(--theme-gradient-from), var(--theme-gradient-via), var(--theme-gradient-to))`,
  };
  const { user, logout, isAuthenticated, canEdit, canDelete, hasPermission } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const [activeView, setActiveView] = useState('dashboard-page');
  const [editingActivity, setEditingActivity] = useState(null);
  const [usersKey, setUsersKey] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [ticketViewKey, setTicketViewKey] = useState(0);
  const [activityListKey, setActivityListKey] = useState(0);
  const [transaksiListKey, setTransaksiListKey] = useState(0);
  const [editingTransaksi, setEditingTransaksi] = useState(null);
  const { 
    activities, 
    loading, 
    error, 
    stats,
    createActivity, 
    updateActivity, 
    deleteActivity, 
    clearError,
    fetchActivities,
    fetchStats 
  } = useActivities(!isAuthenticated);

  const { 
    tickets: allTickets, 
    loading: ticketLoading,
    stats: ticketStats,
    openCount,
    createTicket,
    fetchTickets,
    fetchStats: fetchTicketStats
  } = useTickets(!isAuthenticated);

  // ─── View order for directional transitions ──────────────
  const viewOrder = [
    'dashboard-page', 'add', 'list', 'stats',
    'new-ticket', 'tickets', 'report-tickets',
    'cafe-pos', 'cafe-dashboard', 'cafe-sales', 'cafe-report', 'master-akun', 'transaksi-add', 'transaksi', 'jurnal', 'laporan-akhir',
    'users', 'master-bagian', 'master-cabang', 'log-histories', 'hak-akses', 'rekap-absensi'
  ];

  // ─── Page transition state ───────────────────────────────
  const [transitionState, setTransitionState] = useState('entering');
  const [renderedView, setRenderedView] = useState('dashboard-page');
  const [transitionClass, setTransitionClass] = useState('animate-page-enter-fade');
  const transitionTimerRef = useRef(null);

  // ─── Dropdown state ──────────────────────────────────────
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const closeDropdown = () => setOpenDropdown(null);
  const toggleDropdown = (menu) => setOpenDropdown(prev => prev === menu ? null : menu);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) closeDropdown();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initial enter animation
  useEffect(() => {
    const t = setTimeout(() => setTransitionState('active'), 400);
    return () => clearTimeout(t);
  }, []);

  // ─── Ctrl+Shift+/ to open shortcuts help modal ──────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === '/') {
        e.preventDefault();
        setShowShortcutsModal(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Escape to close shortcuts modal ────────────────────
  useEffect(() => {
    if (!showShortcutsModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowShortcutsModal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showShortcutsModal]);

  // ─── Keyboard shortcuts ────────────────────────────────
  useKeyboardShortcuts({
    1: { view: 'dashboard-page', handler: () => navigateTo('dashboard-page') },
    2: { view: 'add', handler: () => { setEditingActivity(null); navigateTo('add'); }, check: () => canEdit() },
    3: { view: 'list', handler: () => navigateTo('list') },
    4: { view: 'stats', handler: () => navigateTo('stats'), check: () => canEdit() },
    5: { view: 'new-ticket', handler: () => navigateTo('new-ticket'), check: () => canEdit() },
    6: { view: 'tickets', handler: () => navigateTo('tickets') },
    7: { view: 'report-tickets', handler: () => navigateTo('report-tickets'), check: () => canEdit() },
    8: { view: 'users', handler: () => navigateTo('users', { refreshUsers: true }), check: () => user?.role === 'admin' },
    0: {
      view: 'theme-toggle',
      handler: () => {
        const currentIdx = themes.findIndex(t => t.id === theme.id);
        const nextIdx = (currentIdx + 1) % themes.length;
        setTheme(themes[nextIdx].id);
      },
    },
  });

  // ─── Unified navigation ────────────────────────────────
  const navigateTo = (view, { animated = true, refreshUsers = false, refreshActivities = false } = {}) => {
    if (view === renderedView) return;
    
    if (refreshUsers) setUsersKey(k => k + 1);
    if (refreshActivities) {
      setActivityListKey(k => k + 1);
      fetchActivities();
      fetchStats();
    }
    
    closeDropdown();

    if (!animated) {
      clearTimeout(transitionTimerRef.current);
      setRenderedView(view);
      setActiveView(view);
      setTransitionClass('animate-page-enter-fade');
      setTransitionState('entering');
      setTimeout(() => setTransitionState('active'), 400);
      return;
    }

    const prevIdx = viewOrder.indexOf(renderedView);
    const nextIdx = viewOrder.indexOf(view);
    let enterAnim;
    if (nextIdx > prevIdx) enterAnim = 'animate-page-enter-right';
    else if (nextIdx < prevIdx) enterAnim = 'animate-page-enter-left';
    else enterAnim = 'animate-page-enter-fade';

    setTransitionState('leaving');
    clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => {
      setRenderedView(view);
      setActiveView(view);
      setTransitionClass(enterAnim);
      setTransitionState('entering');
      setTimeout(() => setTransitionState('active'), 400);
    }, 250);
  };

  useEffect(() => {
    return () => clearTimeout(transitionTimerRef.current);
  }, []);

  if (!isAuthenticated || !user) {
    if (showRegister) {
      return <RegisterPage onSwitchToLogin={() => setShowRegister(false)} />;
    }
    return <LoginPage onSwitchToRegister={() => setShowRegister(true)} />;
  }

  const handleAddActivity = async (newActivity) => {
    try {
      await createActivity(newActivity);
      setEditingActivity(null);
      navigateTo('dashboard-page', { animated: false, refreshActivities: true });
    } catch (err) {}
  };

  const handleEditClick = (activity) => {
    setEditingActivity(activity);
    navigateTo('add', { animated: false });
  };

  const handleEditSubmit = async (activityData) => {
    try {
      const id = editingActivity._id || editingActivity.id;
      await updateActivity(id, activityData);
      setEditingActivity(null);
      navigateTo('dashboard-page', { animated: false, refreshActivities: true });
    } catch (err) {}
  };

  const handleDeleteActivity = async (id) => {
    try {
      await deleteActivity(id);
    } catch (err) {}
  };

  const handleCreateTransaksi = async (data) => {
    try {
      await financeService.createTransaksi(data);
      setEditingTransaksi(null);
      setTransaksiListKey(k => k + 1);
      navigateTo('transaksi', { animated: false });
    } catch (err) {
      throw err;
    }
  };

  const handleEditTransaksi = async (data) => {
    try {
      const id = editingTransaksi._id;
      await financeService.updateTransaksi(id, data);
      setEditingTransaksi(null);
      setTransaksiListKey(k => k + 1);
      navigateTo('transaksi', { animated: false });
    } catch (err) {
      throw err;
    }
  };

  const handleEditTransaksiClick = (tx) => {
    setEditingTransaksi(tx);
    navigateTo('transaksi-add', { animated: false });
  };

  const getRoleBadge = () => {
    const configs = {
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin', icon: Shield },
      user: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'User', icon: UserIcon },
      viewer: { bg: 'bg-green-100', text: 'text-green-700', label: 'Viewer', icon: UserIcon },
      perawat: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Perawat', icon: HeartPulse },
    };
    const cfg = configs[user.role] || { bg: 'bg-gray-100', text: 'text-gray-700', label: user.role, icon: UserIcon };
    return cfg;
  };

  const roleBadge = getRoleBadge();

  // ─── Menu structure ─────────────────────────────────────
  const menuItems = [
    { 
      id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'user', 'viewer'], menuId: 'DASHBOARD',
      type: 'link', view: 'dashboard-page', shortcut: 'Ctrl+1',
    },
    { 
      id: 'activity', label: 'Activity', icon: Activity, roles: ['admin', 'user', 'viewer'],
      type: 'dropdown',
      children: [
        { id: 'add', label: 'Add Activity', icon: PlusCircle, roles: ['admin', 'user'], view: 'add', shortcut: 'Ctrl+2', menuId: 'ACTIVITY_ADD' },
        { id: 'list', label: 'Activities', icon: List, roles: ['admin', 'user', 'viewer'], view: 'list', shortcut: 'Ctrl+3', menuId: 'ACTIVITY_LIST' },
        { id: 'stats', label: 'Report Activity', icon: FileBarChart, roles: ['admin', 'user'], view: 'stats', shortcut: 'Ctrl+4', menuId: 'ACTIVITY_REPORT' },
        { id: 'rekap-absensi', label: 'Rekap Absensi', icon: UserCheck, roles: ['admin', 'user', 'viewer'], view: 'rekap-absensi', menuId: 'REKAP_ABSENSI' },
      ]
    },
    { 
      id: 'ticketing', label: 'Ticketing', icon: Ticket, roles: ['admin', 'user', 'viewer'],
      type: 'dropdown',
      badge: user.role === 'admin' ? openCount : 0,
      children: [
        { id: 'new-ticket', label: 'New Tickets', icon: PlusSquare, roles: ['admin', 'user'], view: 'new-ticket', shortcut: 'Ctrl+5', menuId: 'TICKET_NEW' },
        { id: 'tickets', label: 'Tickets', icon: List, roles: ['admin', 'user', 'viewer'], view: 'tickets', shortcut: 'Ctrl+6', menuId: 'TICKET_LIST' },
        { id: 'report-tickets', label: 'Report Tickets', icon: FileBarChart, roles: ['admin', 'user'], view: 'report-tickets', shortcut: 'Ctrl+7', menuId: 'TICKET_REPORT' },
      ]
    },
    { 
      id: 'cafe', label: 'Cafe', icon: Coffee, roles: ['admin', 'user'],
      type: 'dropdown',
      children: [
        { id: 'cafe-pos', label: 'POS Kasir', icon: ShoppingCart, roles: ['admin', 'user'], view: 'cafe-pos', menuId: 'CAFE_POS' },
        { id: 'cafe-dashboard', label: 'Menu Produk', icon: Coffee, roles: ['admin', 'user'], view: 'cafe-dashboard', menuId: 'CAFE_PRODUK' },
        { id: 'cafe-sales', label: 'Riwayat Penjualan', icon: History, roles: ['admin', 'user'], view: 'cafe-sales', menuId: 'CAFE_SALES' },
        { id: 'cafe-report', label: 'Laporan Penjualan', icon: FileBarChart, roles: ['admin', 'user'], view: 'cafe-report', menuId: 'CAFE_REPORT' },
        { id: 'print-settings', label: 'Cetak Struk', icon: Printer, roles: ['admin'], view: 'print-settings', menuId: 'CAFE_PRODUK' },
      ]
    },
    { 
      id: 'keuangan', label: 'Keuangan', icon: DollarSign, roles: ['admin', 'user', 'viewer'],
      type: 'dropdown',
      children: [
        { id: 'master-akun', label: 'Master Akun', icon: Banknote, roles: ['admin', 'user'], view: 'master-akun', menuId: 'FINANCE_AKUN' },
        { id: 'transaksi-add', label: 'Add Transaksi', icon: PlusSquare, roles: ['admin', 'user'], view: 'transaksi-add', menuId: 'FINANCE_TRANSAKSI_ADD' },
        { id: 'transaksi', label: 'Transaksi In/Out', icon: List, roles: ['admin', 'user'], view: 'transaksi', menuId: 'FINANCE_TRANSAKSI' },
        { id: 'jurnal', label: 'Jurnal Umum', icon: BookOpen, roles: ['admin', 'user'], view: 'jurnal', menuId: 'FINANCE_JURNAL' },
        { id: 'laporan-akhir', label: 'Laporan Akhir', icon: TrendingUp, roles: ['admin', 'user'], view: 'laporan-akhir', menuId: 'FINANCE_LAPORAN' },
      ]
    },
    { 
      id: 'users', label: 'User', icon: Users, roles: ['admin'],
      type: 'dropdown',
      children: [
        { id: 'users', label: 'Master User', icon: UserIcon, roles: ['admin'], view: 'users', menuId: 'USER_MASTER' },
        { id: 'master-bagian', label: 'Master Bagian', icon: Building2, roles: ['admin'], view: 'master-bagian', menuId: 'USER_BAGIAN' },
        { id: 'master-cabang', label: 'Master Cabang', icon: MapPin, roles: ['admin'], view: 'master-cabang' },
        { id: 'log-histories', label: 'Log Histories', icon: History, roles: ['admin'], view: 'log-histories', menuId: 'USER_LOG' },
        { id: 'hak-akses', label: 'Hak Akses', icon: Settings, roles: ['admin'], view: 'hak-akses', menuId: 'USER_HAK_AKSES' },
      ]
    },
    { 
      id: 'antrian', label: 'Antrian Klinik', icon: HeartPulse, roles: ['admin', 'user', 'viewer', 'perawat'],
      type: 'dropdown',
      children: [
        { id: 'antrian-web', label: 'Antrian Klinik', icon: HeartPulse, roles: ['admin', 'user', 'viewer', 'perawat'], url: '/antrian.html', menuId: 'ANTRIAN_WEB' },
        { id: 'display-tv', label: 'Display TV', icon: Monitor, roles: ['admin', 'user', 'viewer', 'perawat'], url: '/display-tv.html', menuId: 'ANTRIAN_DISPLAY' },
      ]
    },
  ];

  const handleCreateTicket = async (ticketData) => {
    await createTicket(ticketData);
    setTicketViewKey(k => k + 1);
    navigateTo('tickets', { animated: false });
  };

  const handleViewTicket = (ticket) => setSelectedTicket(ticket);

  const handleTicketUpdate = async (updatedTicket) => {
    setSelectedTicket(updatedTicket);
    setTicketViewKey(k => k + 1);
    try { await fetchTickets(); } catch (err) {}
  };

  const renderView = () => {
    // Mobile: tampilkan MobileDashboard untuk halaman utama
    if (isMobile && activeView === 'dashboard-page') {
      return (
        <MobileDashboard 
          activities={activities} stats={stats} tickets={allTickets}
          ticketStats={ticketStats} ticketOpenCount={openCount}
          onNavigate={(view) => navigateTo(view)}
        />
      );
    }
    switch (activeView) {
      case 'dashboard-page':
        return (
          <DashboardPage 
            activities={activities} stats={stats} tickets={allTickets}
            ticketStats={ticketStats} ticketOpenCount={openCount}
            onNavigate={(view) => navigateTo(view)}
          />
        );
      case 'add':
        return <ActivityForm onSubmit={editingActivity ? handleEditSubmit : handleAddActivity} initialData={editingActivity} onCancel={() => navigateTo('dashboard-page', { animated: false })} />;
      case 'list':
        return <ActivityList key={activityListKey} activities={activities} onDelete={canDelete ? handleDeleteActivity : null} onEdit={canEdit ? handleEditClick : null} readOnly={!canEdit} />;
      case 'stats':
        return <ActivityStats activities={activities} stats={stats} />;
      case 'rekap-absensi':
        return <RekapAbsensi activities={activities} />;
      case 'cafe-pos':
        return <CafePOS onNavigate={(view) => navigateTo(view)} />;
      case 'cafe-dashboard':
        return <CafeDashboard />;
      case 'cafe-sales':
        return <CafeSales />;
      case 'cafe-report':
        return <CafeReport />;
      case 'print-settings':
        return <PrintSettings />;
      case 'users':
        return <ErrorBoundary><UserManagement key={usersKey} /></ErrorBoundary>;
      case 'master-bagian':
        return <MasterBagian />;
      case 'master-cabang':
        return <MasterCabang />;
      case 'log-histories':
        return <LogHistories />;
      case 'hak-akses':
        return <HakAkses />;
      case 'master-akun':
        return <MasterAkun />;
      case 'transaksi-add':
        return <TransaksiForm onSubmit={editingTransaksi ? handleEditTransaksi : handleCreateTransaksi} initialData={editingTransaksi} onCancel={() => navigateTo('transaksi', { animated: false })} />;
      case 'transaksi':
        return <TransaksiFinance key={transaksiListKey} onAdd={() => { setEditingTransaksi(null); navigateTo('transaksi-add', { animated: false }); }} onEdit={handleEditTransaksiClick} />;
      case 'jurnal':
        return <JurnalUmum />;
      case 'laporan-akhir':
        return <LaporanAkhir />;
      case 'tickets':
        return <TicketList key={ticketViewKey} tickets={allTickets} onView={handleViewTicket} isAdminView={user.role === 'admin'} />;
      case 'new-ticket':
        return <TicketForm onSubmit={handleCreateTicket} onCancel={() => navigateTo('tickets', { animated: false })} />;
      case 'report-tickets':
        return <TicketReport tickets={allTickets} stats={ticketStats} openCount={openCount} />;
      default:
        return <DashboardPage activities={activities} stats={stats} tickets={allTickets} ticketStats={ticketStats} ticketOpenCount={openCount} onNavigate={(view) => navigateTo(view)} />;
    }
  };

  // ─── Mobile Layout ─────────────────────────────────────
  if (isMobile) {
    return (
      <MobileLayout
        activeView={activeView}
        onNavigate={(view) => {
          if (view === 'add' || view === 'new-ticket' || view === 'transaksi-add') {
            if (view === 'add') setEditingActivity(null);
            if (view === 'transaksi-add') setEditingTransaksi(null);
          }
          navigateTo(view, { animated: false });
        }}
        user={user}
        logout={logout}
        openCount={openCount}
      >
        {/* Error Alert */}
        {error && (
          <div className="mb-3 animate-slide-up">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0"><AlertCircle className="h-5 w-5 text-red-400" /></div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                  <button onClick={clearError} className="mt-2 text-sm font-medium text-red-600 hover:text-red-500 transition-colors">Dismiss</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <Loader2 className="h-8 w-8 animate-spin theme-spinner" />
              <div className="absolute inset-0 h-8 w-8 rounded-full border-4 border-blue-100 animate-ping opacity-20" />
            </div>
            <span className="mt-3 text-gray-500 font-medium text-sm">Loading...</span>
          </div>
        )}

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <TicketDetail ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdate={handleTicketUpdate} />
        )}

        {/* Main Content */}
        {!loading && (
          <div key={renderedView} className={`${transitionState === 'leaving' ? 'animate-page-leave-fade' : transitionClass}`}
            style={{ animationFillMode: 'both', willChange: 'transform, opacity' }}>
            {renderView()}
          </div>
        )}
      </MobileLayout>
    );
  }

  // ─── Desktop Layout ─────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="shadow-lg" style={headerStyle}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-lg p-2 backdrop-blur-sm">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-white">
                <button onClick={() => navigateTo('dashboard-page')} className="text-white hover:text-white/80 transition-colors cursor-pointer">Activity Tracker</button>
              </h1>
            </div>
            
            <div className="flex items-center space-x-2" ref={dropdownRef}>
              {/* Navigation - Desktop */}
              <nav className="hidden md:flex items-center space-x-1">
                {menuItems
                  .filter(item => item.roles.includes(user.role))
                  .filter(item => {
                    // Filter by permission menuId
                    if (item.type === 'link' && item.menuId) {
                      return hasPermission(item.menuId, 'active');
                    }
                    if (item.type === 'dropdown') {
                      const visibleChildren = item.children?.filter(c =>
                        c.roles.includes(user.role) && (!c.menuId || hasPermission(c.menuId, 'active'))
                      ) || [];
                      return visibleChildren.length > 0;
                    }
                    return true;
                  })
                  .map(item => {
                    if (item.type === 'link') {
                      const isActive = activeView === item.view;
                      return (
                        <button key={item.id} onClick={() => navigateTo(item.view)}
                          className={`nav-link relative ${isActive ? 'nav-link-active' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
                          title={`${item.label} (${item.shortcut || ''})`}>
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                          {item.shortcut && <span className="ml-1.5 text-[10px] font-medium text-white/40 hidden xl:inline">{item.shortcut}</span>}
                        </button>
                      );
                    }

                    const isOpen = openDropdown === item.id;
                    const isActive = item.children?.some(c => activeView === c.view);
                    const userChildren = item.children?.filter(c => c.roles.includes(user.role) && (!c.menuId || hasPermission(c.menuId, 'active'))) || [];
                    if (userChildren.length === 0) return null;

                    return (
                      <div key={item.id} className="relative">
                        <button onClick={() => toggleDropdown(item.id)}
                          className={`nav-link relative ${isActive || isOpen ? 'nav-link-active' : 'text-white/80 hover:text-white hover:bg-white/10'}`}>
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          {item.badge > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-white">{item.badge > 99 ? '99+' : item.badge}</span>
                          )}
                        </button>
                        {isOpen && (
                          <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 animate-scale-in origin-top-left">
                            <div className="p-1.5">
                              {userChildren.map(child => {
                                const childActive = activeView === child.view && !child.url;
                                return (
                                  <button key={child.id} onClick={() => { if (child.url) { window.open(child.url, '_blank'); closeDropdown(); } else { if (child.id === 'add') setEditingActivity(null); navigateTo(child.view); } }}
                                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm transition-all ${childActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50 font-medium'}`}>
                                    <div className={`p-1.5 rounded-lg ${childActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                      <child.icon className={`w-4 h-4 ${childActive ? 'text-blue-600' : 'text-gray-500'}`} />
                                    </div>
                                    <span className="flex-1 text-left">{child.label}</span>
                                    {child.shortcut && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${childActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>{child.shortcut?.replace('Ctrl+', '')}</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </nav>

              {/* Keyboard shortcuts indicator */}
              <button onClick={() => setShowShortcutsModal(true)}
                className="hidden xl:flex items-center space-x-1 px-2 py-1 text-white/40 hover:text-white/70 transition-colors text-[10px] font-medium"
                title="Keyboard shortcuts: Ctrl+1-8 (Ctrl+Shift+/ for help)">
                <Keyboard className="w-3.5 h-3.5" /><span>Shortcuts</span>
              </button>

              {/* Brand */}
              <div className="hidden sm:flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                <span className="text-xs font-bold text-white tracking-wide">Agus_Greneng</span>
              </div>

              {/* Theme Switcher */}
              <ThemeSwitcher />

              {/* User Info */}
              <div className="flex items-center space-x-2 pl-3 border-l border-white/20">
                <div className={`hidden sm:flex items-center space-x-1.5 px-2.5 py-1 rounded-lg ${roleBadge.bg}`}>
                  <roleBadge.icon className={`w-3.5 h-3.5 ${roleBadge.text}`} />
                  <span className={`text-xs font-semibold ${roleBadge.text}`}>{roleBadge.label}</span>
                </div>
                {user.bagian && <span className="hidden sm:block text-xs text-white/50">{user.bagian}</span>}
                <span className="hidden sm:block text-sm text-white/80">{user.name || user.username}</span>
                <button onClick={logout} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all" title="Logout">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>

              {/* Mobile Nav */}
              <div className="md:hidden flex items-center space-x-1">
                {menuItems.filter(item => item.roles.includes(user.role)).filter(item => {
                    if (item.type === 'link' && item.menuId) {
                      return hasPermission(item.menuId, 'active');
                    }
                    if (item.type === 'dropdown') {
                      const visibleChildren = item.children?.filter(c =>
                        c.roles.includes(user.role) && (!c.menuId || hasPermission(c.menuId, 'active'))
                      ) || [];
                      return visibleChildren.length > 0;
                    }
                    return true;
                  }).map(item => {
                  if (item.type === 'link') {
                    return (
                      <button key={item.id} onClick={() => navigateTo(item.view)}
                        className={`p-2 rounded-lg transition-all ${activeView === item.view ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`} title={item.label}>
                        <item.icon className="w-5 h-5" />
                      </button>
                    );
                  }
                  const userChildren = item.children?.filter(c => c.roles.includes(user.role) && (!c.menuId || hasPermission(c.menuId, 'active'))) || [];
                  return userChildren.map(child => (
                    <button key={child.id} onClick={() => { if (child.url) window.open(child.url, '_blank'); else { if (child.id === 'add') setEditingActivity(null); navigateTo(child.view); } }}
                      className={`p-2 rounded-lg transition-all ${activeView === child.view ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`} title={child.label}>
                      <child.icon className="w-5 h-5" />
                    </button>
                  ));
                })}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 animate-slide-up">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0"><AlertCircle className="h-5 w-5 text-red-400" /></div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <button onClick={clearError} className="mt-2 text-sm font-medium text-red-600 hover:text-red-500 transition-colors">Dismiss</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <Loader2 className="h-10 w-10 animate-spin theme-spinner" />
              <div className="absolute inset-0 h-10 w-10 rounded-full border-4 border-blue-100 animate-ping opacity-20" />
            </div>
            <span className="mt-4 text-gray-500 font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <TicketDetail ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdate={handleTicketUpdate} />
      )}

      {/* Network Status (PWA Offline Indicator) */}
      <NetworkStatus />

      {/* Update Notification (non-blocking SW update prompt) */}
      <UpdateNotification />

      {/* Keyboard Shortcuts Help Modal */}
      <ShortcutsHelpModal isOpen={showShortcutsModal} onClose={() => setShowShortcutsModal(false)} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!loading && (
          <div key={renderedView} className={`${transitionState === 'leaving' ? 'animate-page-leave-fade' : transitionClass}`}
            style={{ animationFillMode: 'both', willChange: 'transform, opacity' }}>
            {renderView()}
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
